/**
 * UO交易大厅 — single completed sell card (ys142857 parity).
 */
import { memo } from 'react';
import { DEFAULT_USER_AVATAR, OTC_COLORS as C } from '@/constants';
import { formatLegacyPhpDate } from '../../utils/legacy-php-time';

export type UoHallRow = {
  id: string;
  display_id?: string;
  avatar?: string | null;
  listed_uo?: string;
  traded_uo?: string;
  amount_uo?: string;
  filled_uo?: string;
  coin_price?: string;
  unit_price?: string;
  value_ap?: string;
  status_zh?: string;
  create_time: string;
  update_time?: string;
};

export type UoHallRowDisplay = {
  id: string;
  routeId: string;
  displayId: string;
  avatarSrc: string;
  status: string;
  listed: string;
  traded: string;
  price: string;
  value: string;
  date: string;
};

function fmt(v: string | undefined, digits = 4): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

/** Pre-format API rows once per fetch — keeps card renders cheap. */
export function mapUoHallRowDisplay(row: UoHallRow, statusFallback: string): UoHallRowDisplay {
  const displayId = row.display_id ?? row.id.replace(/^php:/, '');
  const avatar = row.avatar?.trim();
  return {
    id: row.id,
    routeId: displayId,
    displayId,
    avatarSrc: avatar || DEFAULT_USER_AVATAR,
    status: row.status_zh ?? statusFallback,
    listed: fmt(row.listed_uo ?? row.amount_uo ?? '0', 1),
    traded: fmt(row.traded_uo ?? row.filled_uo ?? '0', 1),
    price: fmt(row.coin_price ?? row.unit_price ?? '0', 3),
    value: fmt(row.value_ap ?? '0', 2),
    date: formatLegacyPhpDate(row.update_time ?? row.create_time ?? ''),
  };
}

const LABELS = {
  sellTotal: '挂卖总数',
  filled: '已成交',
  coinPrice: '币价',
  value: '价值',
} as const;

const StatCell = memo(function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 text-center">
      <div className="truncate font-mono text-sm font-semibold" style={{ color: C.gold }}>
        {value}
      </div>
      <div className="mt-0.5 truncate text-[10px]" style={{ color: C.muted }}>
        {label}
      </div>
    </div>
  );
});

export const UoHallCard = memo(function UoHallCard({
  row,
  labels = LABELS,
  onOpen,
}: {
  row: UoHallRowDisplay;
  labels?: {
    sellTotal: string;
    filled: string;
    coinPrice: string;
    value: string;
  };
  onOpen: (routeId: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(row.routeId)}
      className="block w-full border-b px-4 py-4 text-left transition-opacity active:opacity-80 [content-visibility:auto] [contain-intrinsic-size:auto_120px]"
      style={{ borderColor: C.line, contain: 'layout style paint' }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <img
            src={row.avatarSrc}
            alt=""
            width={36}
            height={36}
            loading="lazy"
            decoding="async"
            className="h-9 w-9 shrink-0 rounded-full border object-cover"
            style={{ borderColor: C.softBorder, background: C.chipBg }}
          />
          <span className="font-mono text-sm font-semibold" style={{ color: C.gold }}>
            {row.displayId}
          </span>
        </div>
        <span className="shrink-0 text-xs font-semibold" style={{ color: C.gold }}>
          {row.status}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2">
        <StatCell label={labels.sellTotal} value={row.listed} />
        <StatCell label={labels.filled} value={row.traded} />
        <StatCell label={labels.coinPrice} value={row.price} />
        <StatCell label={labels.value} value={row.value} />
      </div>

      <div className="mt-2 text-right text-[10px]" style={{ color: C.muted }}>
        {row.date}
      </div>
    </button>
  );
});
